/** @jsx jsx */
import { css, jsx } from '@emotion/core';

import {
  Colors,
  ColorName,
  Icon,
  IconName,
  Shadows,
  Typography,
} from '@zapier/design-system';

export enum MessageBarType {
  error = 'error',
  info = 'info',
  success = 'success',
  warning = 'warning',
}

type MessageBarProps = {
  /** Children (usually text) to display as the main content of the message. */
  children?: React.ReactNode;
  /** Icon name (will override the default icon for the type) */
  icon?: IconName;
  /** Handler for dismissing the message. */
  onDismiss?: () => void;
  /** Component to display on right side */
  right?: React.ReactNode;
  /** Bolded title to display in the header. */
  title?: string;
  /** The type of message bar to display. */
  type: MessageBarType;
};

const rootStyle = (
  hasIcon: boolean,
  canDismiss: boolean,
  hasRight: boolean
) => css`
  border: 2px solid;
  border-radius: 10px;
  box-shadow: ${Shadows.elevation5};
  color: ${Colors.black};
  display: grid;
  grid-gap: 15px;
  grid-template-columns: ${hasIcon ? '26px ' : ''}1fr ${hasRight ? ' auto' : ''} ${canDismiss
      ? ' 22px'
      : ''};
  padding: 14px;
`;

const bodyStyle = css`
  ${Typography.altText900}
  line-height: 20px;
`;

const dismissStyle = css`
  background: transparent;
  border: none;
  box-sizing: border-box;
  height: 20px;
  margin-top: -2px;
`;

const iconStyle = css`
  height: 20px;
  margin-top: -6px;
`;

const titleStyle = css`
  font-weight: 600;
`;

const TYPES_MAP: {
  [key in MessageBarType]: {
    color: ColorName;
    icon: IconName;
    styles: {};
    title: string;
  };
} = {
  error: {
    color: 'red500',
    icon: 'alertCircle',
    styles: {
      backgroundColor: Colors.red000,
      borderColor: Colors.red500,
    },
    title: 'Bargle!',
  },
  info: {
    color: 'grey500',
    icon: 'alertInfo',
    styles: {
      backgroundColor: Colors.grey000,
      borderColor: Colors.grey500,
    },
    title: '',
  },
  success: {
    color: 'green400',
    icon: 'formCheckCircle',
    styles: {
      backgroundColor: Colors.green300,
      borderColor: Colors.green400,
    },
    title: 'Success!',
  },
  warning: {
    color: 'yellow500',
    icon: 'alertInfo',
    styles: {
      backgroundColor: Colors.yellow200,
      borderColor: Colors.yellow500,
    },
    title: 'Warning.',
  },
};

function MessageBar(props: MessageBarProps) {
  const type = TYPES_MAP[props.type] || TYPES_MAP.info;
  const icon = props.icon !== undefined ? props.icon : type.icon;
  const title = props.title !== undefined ? props.title : type.title;
  const canDismiss = !!props.onDismiss;

  return (
    <div css={[rootStyle(!!icon, canDismiss, !!props.right), type.styles]}>
      {!!icon && (
        <div css={iconStyle}>
          <Icon color={type.color} name={icon} size={32} />
        </div>
      )}

      <div css={bodyStyle}>
        {!!title && <strong css={titleStyle}>{title}</strong>} {props.children}
      </div>

      {props.right}

      {canDismiss && (
        <button css={dismissStyle} onClick={props.onDismiss}>
          <Icon color={type.color} name="formX" size={24} />
        </button>
      )}
    </div>
  );
}

MessageBar.defaultProps = {
  type: 'info',
};

export default MessageBar;
